import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { tasksApi } from "../lib/api";
import { useAuth } from "./AuthContext";

const asLocalDay = (s) => {
  if (!s) return null;
  const d = s.length === 10 ? new Date(`${s}T00:00:00`) : new Date(s);
  return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/**
 * Shows a one-time in-app reminder toast (per session) when the logged-in user
 * has open tasks due today or overdue. Re-checks every 15 minutes; the toast is
 * only raised once per session to avoid nagging.
 */
export function TaskReminderProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fired = useRef(false);
  const authed = user && user !== false && user.role !== "super_admin";

  useEffect(() => {
    if (!authed) {
      fired.current = false;
      return;
    }
    let cancelled = false;

    const check = async () => {
      try {
        const tasks = await tasksApi.list({ mine: true, status: "open" });
        if (cancelled || fired.current) return;
        const t0 = asLocalDay(new Date().toISOString());
        let due = 0;
        let over = 0;
        tasks.forEach((t) => {
          const d = asLocalDay(t.dueAt);
          if (!d) return;
          const diff = Math.round((d - t0) / 86400000);
          if (diff < 0) over += 1;
          else if (diff === 0) due += 1;
        });
        if (due + over > 0) {
          fired.current = true;
          const parts = [];
          if (over) parts.push(`${over} overdue`);
          if (due) parts.push(`${due} due today`);
          toast.warning("Task reminder", {
            description: `You have ${parts.join(" and ")}.`,
            duration: 3000,
            position: "top-center",
            action: { label: "View tasks", onClick: () => navigate("/tasks") },
          });
        }
      } catch {
        /* non-critical */
      }
    };

    check();
    const id = setInterval(check, 15 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [authed, navigate]);

  return children;
}
